// Pages/inventario/components/HistoriaProductoDrawer.tsx
//
// LÍNEA DE TIEMPO DE UN PRODUCTO (round 2026-08-06).
//
// Responde de un vistazo "¿de dónde salió esta cantidad y qué pasó con ella?":
// cuándo se compró, cuánto costó, a qué bodega/bodeguita/propiedad se fue
// moviendo, qué se corrigió, si se vendió/perdió/robó/rompió, y cuánta plata
// implicó cada paso (costo de AF, cobro al cliente, reembolso al tercero).
//
// Se abre desde tres lugares:
//   • la fila de un producto en /inventario,
//   • una transacción de la pestaña Finanzas (incluidas las HUÉRFANAS, cuyo
//     producto ya no existe — el endpoint acepta ids borrados),
//   • el modal de borrado de una propiedad, para decidir con contexto qué
//     hacer con cada compra antes de eliminarla.
//
// El eje vertical es el tiempo. Cada evento muestra su delta de unidades y el
// SALDO acumulado, que es la "evolución de la cantidad": lo que el owner pidió
// poder seguir visualmente hacia atrás.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Drawer, Empty, Spin, Tag, Timeline, Tooltip, Typography } from "antd";
import {
  ArrowRightOutlined,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  RollbackOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { inventarioApi } from "../../../services/inventario.api";
import { formatPropertyName } from "../../../utils/propertyDisplay";
import "../inventario.css";
import "./historiaProducto.css";

const { Text, Title } = Typography;

const clp = (n: number | null | undefined) =>
  `$${Number(n || 0).toLocaleString("es-CL")}`;

/** Ubicación tal como la devuelve el backend (snapshot + si todavía existe). */
interface RefUbicacion {
  tipo: string | null;
  tipoEtiqueta: string;
  id: number | null;
  nombre: string | null;
  existe: boolean;
}

interface EventoHistoria {
  clave: string;
  fecha: string;
  tipo:
    | "compra"
    | "entrada"
    | "salida"
    | "traslado"
    | "edicion"
    | "baja"
    | "reingreso";
  titulo: string;
  detalle: string | null;
  origen: RefUbicacion | null;
  destino: RefUbicacion | null;
  unidades: number;
  delta: number;
  saldo: number;
  monto: number;
  deshecho: boolean;
  quien: string | null;
  transaccionId: number | null;
  flujos: {
    id: number;
    rol: "recibe" | "paga";
    monto: number;
    estadoPago: string | null;
    contraparte: string | null;
  }[];
}

interface Historia {
  producto: {
    id: number;
    existe: boolean;
    nombre: string | null;
    categoria: string | null;
    marca: string | null;
    cantidadUnidades: number;
    oculto: boolean;
    ocultoRazonEtiqueta: string | null;
    ocultoDetalle: string | null;
    ubicacion: RefUbicacion | null;
  };
  linaje: { id: number; esActual: boolean; nivel: number }[];
  eventos: EventoHistoria[];
  resumen: {
    eventos: number;
    primeraFecha: string | null;
    ultimaFecha: string | null;
    totalComprado: number;
    totalRecuperado: number;
    totalReembolsado: number;
    costoNetoAF: number;
    saldoUnidades: number;
    saldoCuadra: boolean;
  };
}

/** Color e ícono por tipo de evento. Mismo lenguaje visual que el resto del ERP. */
const ESTILO_EVENTO: Record<
  EventoHistoria["tipo"],
  { color: string; icono: React.ReactNode }
> = {
  compra: { color: "green", icono: <ShoppingCartOutlined /> },
  entrada: { color: "blue", icono: <ArrowRightOutlined /> },
  salida: { color: "orange", icono: <SwapOutlined /> },
  traslado: { color: "cyan", icono: <SwapOutlined /> },
  edicion: { color: "gray", icono: <EditOutlined /> },
  baja: { color: "red", icono: <DeleteOutlined /> },
  reingreso: { color: "purple", icono: <RollbackOutlined /> },
};

/**
 * Una ubicación. Si ya no existe lo dice en vez de mostrar un guion: el nombre
 * viene del snapshot guardado en la transacción, así que sobrevive al borrado.
 */
const Ubicacion: React.FC<{ ref: RefUbicacion | null }> = ({ ref: ubi }) => {
  if (!ubi || (!ubi.nombre && !ubi.tipo)) return <Text type="secondary">—</Text>;
  const nombre =
    ubi.tipo === "propiedades" && ubi.nombre
      ? formatPropertyName(ubi.nombre)
      : (ubi.nombre ?? ubi.tipoEtiqueta);
  return (
    <Tooltip title={ubi.tipoEtiqueta}>
      <span className="historia-ubicacion">
        {nombre}
        {!ubi.existe && ubi.id != null && (
          <Tag color="default" className="historia-tag-eliminada">
            eliminada
          </Tag>
        )}
      </span>
    </Tooltip>
  );
};

interface Props {
  /** Id del producto. Puede ser uno ya borrado: se reconstruye igual. */
  inventarioId: number | null;
  open: boolean;
  onClose: () => void;
  /** Nombre de respaldo cuando el producto ya no existe. */
  nombreFallback?: string | null;
}

const HistoriaProductoDrawer: React.FC<Props> = ({
  inventarioId,
  open,
  onClose,
  nombreFallback,
}) => {
  const [cargando, setCargando] = useState(false);
  const [historia, setHistoria] = useState<Historia | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    if (!inventarioId) return;
    setCargando(true);
    setError(null);
    const res = await inventarioApi.getHistoria(inventarioId);
    if (res?.data) {
      setHistoria(res.data);
    } else {
      setHistoria(null);
      setError(
        "No se pudo reconstruir la historia de este producto. Puede que no haya quedado ningún rastro.",
      );
    }
    setCargando(false);
  }, [inventarioId]);

  useEffect(() => {
    if (open && inventarioId) void cargar();
    if (!open) setHistoria(null);
  }, [open, inventarioId, cargar]);

  const p = historia?.producto;
  const r = historia?.resumen;

  return (
    <Drawer
      title={
        <div className="historia-header">
          <span>Historia del producto</span>
          <Text type="secondary" className="historia-header-sub">
            {p?.nombre ?? nombreFallback ?? `#${inventarioId ?? ""}`}
          </Text>
        </div>
      }
      open={open}
      onClose={onClose}
      width={720}
      destroyOnClose
    >
      {cargando && (
        <div className="historia-cargando">
          <Spin tip="Reconstruyendo la historia…" />
        </div>
      )}

      {!cargando && error && <Alert type="warning" showIcon message={error} />}

      {!cargando && historia && (
        <>
          {/* Encabezado: qué es y dónde está hoy */}
          <div className="historia-ficha">
            <div>
              <Title level={5} className="historia-ficha-nombre">
                {p?.nombre ?? "Producto sin nombre"}{" "}
                <Tag color="default" className="inventario-id-tag">
                  PROD #{p?.id}
                </Tag>
              </Title>
              <div className="historia-ficha-meta">
                {p?.categoria && <Tag color="blue">{p.categoria}</Tag>}
                {p?.marca && <Tag>{p.marca}</Tag>}
                {!p?.existe && (
                  <Tag color="red" icon={<ExclamationCircleOutlined />}>
                    el producto ya no existe
                  </Tag>
                )}
                {p?.oculto && (
                  <Tag color="orange">
                    dado de baja
                    {p.ocultoRazonEtiqueta ? ` — ${p.ocultoRazonEtiqueta}` : ""}
                  </Tag>
                )}
              </div>
              {p?.existe && (
                <div className="historia-ficha-ubicacion">
                  Hoy está en <Ubicacion ref={p.ubicacion ?? null} /> con{" "}
                  <strong>{p.cantidadUnidades}</strong> unidades.
                </div>
              )}
            </div>
          </div>

          {/* Plata: las cuatro cifras que importan */}
          <div className="historia-cifras">
            <div className="historia-cifra">
              <span className="historia-cifra-label">Costó (AF puso)</span>
              <span className="historia-cifra-valor">{clp(r?.totalComprado)}</span>
            </div>
            <div className="historia-cifra">
              <span className="historia-cifra-label">Se le cobró al cliente</span>
              <span className="historia-cifra-valor">{clp(r?.totalRecuperado)}</span>
            </div>
            <div className="historia-cifra">
              <span className="historia-cifra-label">Se le debe a terceros</span>
              <span className="historia-cifra-valor">{clp(r?.totalReembolsado)}</span>
            </div>
            <div className="historia-cifra historia-cifra-neto">
              <span className="historia-cifra-label">Costo neto para AF</span>
              <span className="historia-cifra-valor">{clp(r?.costoNetoAF)}</span>
            </div>
          </div>

          {historia.linaje.length > 1 && (
            <Alert
              type="info"
              showIcon
              className="historia-alerta"
              message="Esta mercadería viene de otro producto"
              description={`La historia incluye ${historia.linaje.length - 1} fila(s) anterior(es) de inventario: al mover stock, la parte que se va puede nacer como un producto nuevo en el destino. Se muestran juntas para que la historia no empiece a mitad de camino.`}
            />
          )}

          {r && !r.saldoCuadra && (
            <Alert
              type="warning"
              showIcon
              className="historia-alerta"
              message="El saldo calculado no coincide con el stock actual"
              description={`Sumando los eventos dan ${r.saldoUnidades} unidades, pero el producto tiene ${p?.cantidadUnidades}. Suele pasar cuando se editó el stock a mano sin dejar rastro, o cuando parte del historial se borró.`}
            />
          )}

          {/* La línea de tiempo */}
          {historia.eventos.length === 0 ? (
            <Empty description="Sin eventos registrados" />
          ) : (
            <Timeline
              className="historia-timeline"
              mode="left"
              items={historia.eventos.map((ev) => {
                const estilo = ESTILO_EVENTO[ev.tipo] ?? ESTILO_EVENTO.edicion;
                return {
                  color: ev.deshecho ? "gray" : estilo.color,
                  dot: estilo.icono,
                  children: (
                    <div
                      className={`historia-evento${ev.deshecho ? " historia-evento-deshecho" : ""}`}
                    >
                      <div className="historia-evento-linea1">
                        <strong>{ev.titulo}</strong>
                        <span className="historia-evento-fecha">{ev.fecha}</span>
                        {/* Código de la transacción: es el mismo número que
                            aparece en la pestaña Finanzas, así que se puede
                            saltar de una vista a la otra sin adivinar. */}
                        {ev.transaccionId != null && (
                          <Tooltip title="Código de la transacción en Finanzas">
                            <Tag color="default" className="inventario-id-tag">
                              TX #{ev.transaccionId}
                            </Tag>
                          </Tooltip>
                        )}
                        {ev.deshecho && <Tag>deshecho</Tag>}
                      </div>

                      {(ev.origen || ev.destino) && (
                        <div className="historia-evento-ruta">
                          <Ubicacion ref={ev.origen} />
                          <ArrowRightOutlined className="historia-flecha" />
                          <Ubicacion ref={ev.destino} />
                        </div>
                      )}

                      <div className="historia-evento-cifras">
                        {ev.delta !== 0 && (
                          <Tag color={ev.delta > 0 ? "green" : "orange"}>
                            {ev.delta > 0 ? "+" : ""}
                            {ev.delta} u.
                          </Tag>
                        )}
                        <Tooltip title="Unidades acumuladas después de este evento">
                          <Tag color="default">saldo: {ev.saldo} u.</Tag>
                        </Tooltip>
                        {ev.monto > 0 && <Tag color="gold">{clp(ev.monto)}</Tag>}
                      </div>

                      {ev.flujos.length > 0 && (
                        <div className="historia-evento-flujos">
                          {ev.flujos.map((f) => (
                            <Tooltip
                              key={f.id}
                              title={
                                f.rol === "recibe"
                                  ? "Cobro al cliente por este producto"
                                  : "Reembolso a quien adelantó la plata"
                              }
                            >
                              <Tag color={f.rol === "recibe" ? "blue" : "purple"}>
                                {f.rol === "recibe" ? "cobro" : "reembolso"}{" "}
                                {clp(f.monto)}
                                {f.contraparte ? ` · ${f.contraparte}` : ""}
                                {f.estadoPago === "pendiente" ? " · pendiente" : ""}
                              </Tag>
                            </Tooltip>
                          ))}
                        </div>
                      )}

                      {ev.detalle && (
                        <div className="historia-evento-detalle">{ev.detalle}</div>
                      )}
                      {ev.quien && (
                        <div className="historia-evento-quien">Registró: {ev.quien}</div>
                      )}
                    </div>
                  ),
                };
              })}
            />
          )}
        </>
      )}
    </Drawer>
  );
};

export default HistoriaProductoDrawer;
